import React, { useEffect, useState } from 'react';
import { Card, Form, Select, InputNumber, Button, Space, Typography, message } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useUpdateSurveyMutation } from '../../../services/nestjs-base-api';

const { Text } = Typography;

interface SurveyIntegritySettingsProps {
    surveyId: number;
    initialPanelPartner: string | null;
    initialDetectorProfile: string | null;
    initialLlmBudgetOverrideUsd: number | null;
}

export const SurveyIntegritySettings: React.FC<SurveyIntegritySettingsProps> = ({
    surveyId,
    initialPanelPartner,
    initialDetectorProfile,
    initialLlmBudgetOverrideUsd,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [updateSurvey, { isLoading }] = useUpdateSurveyMutation();

    const [panelPartner, setPanelPartner] = useState<string | null>(initialPanelPartner);
    const [detectorProfile, setDetectorProfile] = useState<string | null>(initialDetectorProfile);
    const [llmBudget, setLlmBudget] = useState<number | null>(initialLlmBudgetOverrideUsd);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setPanelPartner(initialPanelPartner);
        setDetectorProfile(initialDetectorProfile);
        setLlmBudget(initialLlmBudgetOverrideUsd);
        setDirty(false);
    }, [initialPanelPartner, initialDetectorProfile, initialLlmBudgetOverrideUsd]);

    const handleSave = async () => {
        try {
            await updateSurvey({
                jwtToken: user?.jwtToken || '',
                surveyId,
                payload: {
                    panelPartner: panelPartner ?? null,
                    detectorProfile: detectorProfile ?? null,
                    llmBudgetOverrideUsd: llmBudget ?? null,
                } as any,
            }).unwrap();
            message.success('Integrity settings saved');
            setDirty(false);
        } catch (e) {
            message.error('Failed to save integrity settings');
        }
    };

    return (
        <Card
            title={
                <Space>
                    <SafetyCertificateOutlined />
                    <FormattedMessage id="survey.integrity.title" />
                </Space>
            }
        >
            <Form layout="vertical">
                <Form.Item
                    label={<FormattedMessage id="survey.integrity.panelPartner" />}
                    extra={<FormattedMessage id="survey.integrity.panelPartnerHint" />}
                >
                    <Select
                        allowClear
                        value={panelPartner ?? undefined}
                        onChange={(v) => {
                            setPanelPartner(v ?? null);
                            setDirty(true);
                        }}
                        placeholder="None (customer-list)"
                        options={[
                            { value: 'pure_profile', label: 'Pure Profile' },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    label={<FormattedMessage id="survey.integrity.detectorProfile" />}
                    extra={<FormattedMessage id="survey.integrity.detectorProfileHint" />}
                >
                    <Select
                        allowClear
                        value={detectorProfile ?? undefined}
                        onChange={(v) => {
                            setDetectorProfile(v ?? null);
                            setDirty(true);
                        }}
                        placeholder={intl.formatMessage({
                            id: 'survey.integrity.detectorProfile.auto',
                        })}
                        options={[
                            {
                                value: 'light_touch',
                                label: intl.formatMessage({
                                    id: 'survey.integrity.detectorProfile.light_touch',
                                }),
                            },
                            {
                                value: 'full_panel',
                                label: intl.formatMessage({
                                    id: 'survey.integrity.detectorProfile.full_panel',
                                }),
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    label={<FormattedMessage id="survey.integrity.llmBudget" />}
                    extra={<FormattedMessage id="survey.integrity.llmBudgetHint" />}
                >
                    <InputNumber
                        value={llmBudget}
                        onChange={(v) => {
                            setLlmBudget(typeof v === 'number' ? v : null);
                            setDirty(true);
                        }}
                        min={0}
                        step={1}
                        precision={2}
                        style={{ width: 200 }}
                        placeholder="Platform default"
                    />
                </Form.Item>
                <Form.Item>
                    <Button
                        type="primary"
                        loading={isLoading}
                        disabled={!dirty}
                        onClick={handleSave}
                    >
                        Save integrity settings
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
